// Icon types for achievements - used by UI to render Lucide icons
export type AchievementIconType =
  | 'refresh'
  | 'compass'
  | 'arrow-up'
  | 'flame'
  | 'shield'
  | 'calendar'
  | 'zap'
  | 'star'
  | 'brain'
  | 'graduation-cap'
  | 'trophy'
  | 'crown'
  | 'wind'
  | 'heart'
  | 'smile'
  | 'eye'
  | 'target';

export interface Achievement {
  id: string;
  label: string;
  icon: AchievementIconType;
  description: string;
  category: 'beginner' | 'mastery' | 'streak' | 'special' | 'wellness' | 'brain-gym' | 'srl';
  points: number;
  requirement: string;
  check: (progress: AchievementProgress) => boolean;
}

export interface AchievementProgress {
  completedAWU: number;
  completedModules: string[];
  streak: number;
  level: number;
  brainPoints: number;
  // Enhanced tracking
  moodCheckIns?: number;
  techniqueSessions?: number;
  uniqueTechniques?: number;
  stressImproved?: number; // Number of times stress went down after technique
  // Goal tracking
  goalsCreated?: number;
  goalsCompleted?: number;
  milestonesCompleted?: number;
  goalReflectionsWritten?: number;
  uniqueGoalTypes?: number; // Number of different goal categories used
  // Brain Gym / EF tracking
  efGamesPlayed?: number;
  efUniqueGamesPlayed?: number;
  efPersonalBestsCount?: number;
  // SRL tracking
  srlFullCycles?: number;
  srlTotalSessions?: number;
}

export const ACHIEVEMENTS: Achievement[] = [
  // Beginner achievements
  {
    id: 'first-shift',
    label: 'First Strategy Shift',
    icon: 'refresh',
    description: 'Complete your first AWU Protocol and learn to reframe stress.',
    category: 'beginner',
    points: 25,
    requirement: 'Complete 1 AWU Protocol',
    check: (p) => p.completedAWU >= 1,
  },
  {
    id: 'neural-explorer',
    label: 'Neural Explorer',
    icon: 'compass',
    description: 'Begin your journey into understanding your brain.',
    category: 'beginner',
    points: 25,
    requirement: 'Complete 1 learning module',
    check: (p) => p.completedModules.length >= 1,
  },
  {
    id: 'level-2',
    label: 'Leveling Up',
    icon: 'arrow-up',
    description: 'Reach Level 2 and prove your commitment to growth.',
    category: 'beginner',
    points: 15,
    requirement: 'Reach Level 2',
    check: (p) => p.level >= 2,
  },

  // Streak achievements
  {
    id: 'week-streak',
    label: '7-Day Streak',
    icon: 'flame',
    description: 'Practice consistently for a full week.',
    category: 'streak',
    points: 50,
    requirement: 'Maintain a 7-day streak',
    check: (p) => p.streak >= 7,
  },
  {
    id: 'two-week-warrior',
    label: 'Two-Week Warrior',
    icon: 'shield',
    description: 'Two weeks of consistent practice - you\'re building real habits.',
    category: 'streak',
    points: 75,
    requirement: 'Maintain a 14-day streak',
    check: (p) => p.streak >= 14,
  },
  {
    id: 'monthly-master',
    label: 'Monthly Master',
    icon: 'calendar',
    description: 'A full month of dedication. Your neural pathways are transforming.',
    category: 'streak',
    points: 150,
    requirement: 'Maintain a 30-day streak',
    check: (p) => p.streak >= 30,
  },

  // Mastery achievements
  {
    id: 'stress-shifter',
    label: 'Stress Shifter',
    icon: 'zap',
    description: 'Complete 5 AWU sessions and master the art of stress reframing.',
    category: 'mastery',
    points: 50,
    requirement: 'Complete 5 AWU Protocols',
    check: (p) => p.completedAWU >= 5,
  },
  {
    id: 'stress-master',
    label: 'Stress Master',
    icon: 'star',
    description: 'Complete 10 AWU sessions - stress is now your ally.',
    category: 'mastery',
    points: 100,
    requirement: 'Complete 10 AWU Protocols',
    check: (p) => p.completedAWU >= 10,
  },
  {
    id: 'brain-builder',
    label: 'Brain Builder',
    icon: 'brain',
    description: 'Complete all learning modules and become a neuroplasticity expert.',
    category: 'mastery',
    points: 100,
    requirement: 'Complete all 4 learning modules',
    check: (p) => p.completedModules.length >= 4,
  },

  // Special achievements
  {
    id: 'resilience-scholar',
    label: 'Resilience Scholar',
    icon: 'graduation-cap',
    description: 'Reach Level 5 - you truly understand the power of resilience.',
    category: 'special',
    points: 100,
    requirement: 'Reach Level 5',
    check: (p) => p.level >= 5,
  },
  {
    id: 'neural-champion',
    label: 'Neural Champion',
    icon: 'trophy',
    description: 'Earn 1000 Growth Points through consistent effort.',
    category: 'special',
    points: 50,
    requirement: 'Earn 1,000 total GP',
    check: (p) => p.brainPoints >= 1000,
  },
  {
    id: 'grand-master',
    label: 'Grand Master',
    icon: 'crown',
    description: 'Reach Level 10 - the pinnacle of mental resilience.',
    category: 'special',
    points: 200,
    requirement: 'Reach Level 10',
    check: (p) => p.level >= 10,
  },

  // Wellness achievements - behavior-linked
  {
    id: 'first-breath',
    label: 'First Breath',
    icon: 'wind',
    description: 'Complete your first breathing or coping technique.',
    category: 'wellness',
    points: 15,
    requirement: 'Complete 1 technique session',
    check: (p) => (p.techniqueSessions ?? 0) >= 1,
  },
  {
    id: 'calm-collector',
    label: 'Calm Collector',
    icon: 'wind',
    description: 'Try multiple calming techniques to find what works for you.',
    category: 'wellness',
    points: 40,
    requirement: 'Complete 5 technique sessions',
    check: (p) => (p.techniqueSessions ?? 0) >= 5,
  },
  {
    id: 'technique-explorer',
    label: 'Technique Explorer',
    icon: 'eye',
    description: 'Expand your toolkit by trying different techniques.',
    category: 'wellness',
    points: 50,
    requirement: 'Try 3 different techniques',
    check: (p) => (p.uniqueTechniques ?? 0) >= 3,
  },
  {
    id: 'mood-tracker',
    label: 'Mood Tracker',
    icon: 'smile',
    description: 'Check in with yourself regularly.',
    category: 'wellness',
    points: 30,
    requirement: 'Complete 7 mood check-ins',
    check: (p) => (p.moodCheckIns ?? 0) >= 7,
  },
  {
    id: 'self-aware',
    label: 'Self Aware',
    icon: 'heart',
    description: 'Develop a habit of tuning into how you feel.',
    category: 'wellness',
    points: 75,
    requirement: 'Complete 30 mood check-ins',
    check: (p) => (p.moodCheckIns ?? 0) >= 30,
  },
  {
    id: 'stress-transformer',
    label: 'Five Practice Sessions',
    icon: 'target',
    description: 'You have recorded five technique practices. This badge records participation.',
    category: 'wellness',
    points: 60,
    requirement: 'Record five technique practices',
    check: (p) => (p.techniqueSessions ?? 0) >= 5,
  },

  // Goal achievements
  {
    id: 'goal-setter',
    label: 'Goal Setter',
    icon: 'target',
    description: 'You\'ve taken the first step by setting a meaningful goal.',
    category: 'beginner',
    points: 15,
    requirement: 'Create your first goal',
    check: (p) => (p.goalsCreated ?? 0) >= 1,
  },
  {
    id: 'milestone-master',
    label: 'Milestone Master',
    icon: 'target',
    description: 'Progress happens one step at a time.',
    category: 'mastery',
    points: 20,
    requirement: 'Complete 5 milestones',
    check: (p) => (p.milestonesCompleted ?? 0) >= 5,
  },
  {
    id: 'first-goal-complete',
    label: 'Achievement Unlocked',
    icon: 'trophy',
    description: 'You did it! Your first completed goal proves growth is possible.',
    category: 'special',
    points: 50,
    requirement: 'Complete your first goal',
    check: (p) => (p.goalsCompleted ?? 0) >= 1,
  },
  {
    id: 'reflective-achiever',
    label: 'Reflective Achiever',
    icon: 'eye',
    description: 'Learning from your journey makes you unstoppable.',
    category: 'mastery',
    points: 0,
    requirement: 'Write 3 goal reflections',
    check: (p) => (p.goalReflectionsWritten ?? 0) >= 3,
  },
  {
    id: 'goal-crusher',
    label: 'Goal Crusher',
    icon: 'star',
    description: 'Three goals down! You\'re building real momentum.',
    category: 'special',
    points: 75,
    requirement: 'Complete 3 goals',
    check: (p) => (p.goalsCompleted ?? 0) >= 3,
  },
  {
    id: 'well-rounded',
    label: 'Well-Rounded',
    icon: 'compass',
    description: 'Growing in multiple areas shows true versatility.',
    category: 'mastery',
    points: 40,
    requirement: 'Complete goals in 3+ different categories',
    check: (p) => (p.uniqueGoalTypes ?? 0) >= 3,
  },

  // ============================================
  // BRAIN GYM / EXECUTIVE FUNCTION ACHIEVEMENTS
  // ============================================
  {
    id: 'first-workout',
    label: 'First Workout',
    icon: 'brain',
    description: 'You completed your first Brain Gym session! Your executive functions are warming up.',
    category: 'brain-gym',
    points: 20,
    requirement: 'Complete 1 Brain Gym game',
    check: (p) => (p.efGamesPlayed ?? 0) >= 1,
  },
  {
    id: 'brain-explorer',
    label: 'Brain Explorer',
    icon: 'compass',
    description: 'You\'ve tried all 5 Brain Gym games. Each one trains a different cognitive skill!',
    category: 'brain-gym',
    points: 40,
    requirement: 'Play all 5 different Brain Gym games',
    check: (p) => (p.efUniqueGamesPlayed ?? 0) >= 5,
  },
  {
    id: 'brain-athlete',
    label: 'Brain Athlete',
    icon: 'trophy',
    description: 'Setting personal bests means your brain is getting stronger. Keep training!',
    category: 'brain-gym',
    points: 50,
    requirement: 'Achieve personal bests in 3 different games',
    check: (p) => (p.efPersonalBestsCount ?? 0) >= 3,
  },
  {
    id: 'ef-champion',
    label: 'EF Champion',
    icon: 'crown',
    description: 'Twenty games played! Your inhibition, memory, and flexibility are leveling up.',
    category: 'brain-gym',
    points: 75,
    requirement: 'Complete 20 Brain Gym sessions',
    check: (p) => (p.efGamesPlayed ?? 0) >= 20,
  },

  // ============================================
  // SRL (SELF-REGULATED LEARNING) ACHIEVEMENTS
  // ============================================
  {
    id: 'first-full-cycle',
    label: 'First Full Cycle',
    icon: 'refresh',
    description: 'You completed your first full SRL cycle: Plan, Do, Reflect. That\'s real self-regulation!',
    category: 'srl',
    points: 0,
    requirement: 'Complete 1 full SRL cycle (forethought + activity + reflection)',
    check: (p) => (p.srlFullCycles ?? 0) >= 1,
  },
  {
    id: 'strategic-learner',
    label: 'Strategic Learner',
    icon: 'target',
    description: 'Five complete learning cycles means you\'re building a habit of intentional learning.',
    category: 'srl',
    points: 0,
    requirement: 'Complete 5 full SRL cycles',
    check: (p) => (p.srlFullCycles ?? 0) >= 5,
  },
  {
    id: 'self-regulated',
    label: 'Self-Regulated',
    icon: 'star',
    description: 'Fifteen cycles! You\'re becoming a truly self-regulated learner.',
    category: 'srl',
    points: 75,
    requirement: 'Complete 15 full SRL cycles',
    check: (p) => (p.srlFullCycles ?? 0) >= 15,
  },
  {
    id: 'metacognitive-master',
    label: 'Metacognitive Master',
    icon: 'brain',
    description: 'You\'ve engaged with the learning cycle 30 times. You think about your thinking!',
    category: 'srl',
    points: 100,
    requirement: 'Complete 30 SRL sessions (any phase)',
    check: (p) => (p.srlTotalSessions ?? 0) >= 30,
  },
];

export function checkAchievements(
  progress: AchievementProgress,
  currentAchievements: string[]
): Achievement[] {
  const newlyUnlocked: Achievement[] = [];

  for (const achievement of ACHIEVEMENTS) {
    if (!currentAchievements.includes(achievement.id) && achievement.check(progress)) {
      newlyUnlocked.push(achievement);
    }
  }

  return newlyUnlocked;
}

export function getAchievementById(id: string): Achievement | undefined {
  return ACHIEVEMENTS.find(a => a.id === id);
}

export function getAchievementsByCategory(category: Achievement['category']): Achievement[] {
  return ACHIEVEMENTS.filter(a => a.category === category);
}

